<template>
  <div class="ps-overview" :class="{ 'is-course-page': isTwoLevel }">
    <div v-if="startTime || endTime" class="ps-overview__time">
      申报时间：{{ startTime || "-" }} ~ {{ endTime || "-" }}
      <span v-if="!btnShow && canManageProjects" class="ps-overview__time-warn"
        >（当前不在申报期内，操作按钮已隐藏）</span
      >
    </div>

    <ProjectStatBar :module-type="moduleType" :stats="stats" />

    <div class="ps-overview__main">
      <ProjectFilterBar
        :module-type="moduleType"
        :keyword.sync="filters.keyword"
        :audit-state.sync="filters.auditState"
        :year.sync="filters.year"
        :show-add="btnShow && canManageProjects"
        :show-batch-submit="btnShow && canBatchSubmit && !isTwoLevel"
        @add="handleAdd"
        @batch-submit="handleBatchSubmit"
        @change="onFilterChange"
        @search="onSearch"
      />

      <a-spin :spinning="loading">
        <ProjectCardGrid
          :module-type="moduleType"
          :list="dataList"
          :request-count-key="'auditNum'"
          :show-select-all="btnShow && canBatchSubmit && !isTwoLevel"
          :selected-ids="selectedIds"
          :can-select-fn="canSelect"
          @open="handlePreview"
          @preview="handleFormPreview"
          @current="handlePreview"
          @history="(r) => goDetail(r, 'history')"
          @requests="(r) => goDetail(r, 'requests')"
          @select-change="onCardSelectChange"
          @select-all="onSelectAll"
        >
          <template #actions="{ record }">
            <div
              v-if="
                btnShow &&
                canManageProjects &&
                (canEdit(record) || canRemove(record))
              "
              class="ps-more"
              @click.stop
            >
              <a-dropdown :trigger="['hover']">
                <button type="button" class="ps-more-btn" @click.prevent.stop>
                  操作
                  <a-icon type="down" />
                </button>
                <a-menu
                  slot="overlay"
                  @click="(e) => onMoreAction(e, record)"
                >
                  <a-menu-item v-if="canEdit(record)" key="edit">
                    编辑
                  </a-menu-item>
                  <a-menu-item v-if="canRemove(record)" key="remove">
                    <span class="ps-more-danger">移除</span>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>
            <button
              v-if="!isTwoLevel && btnShow && canSubmit(record)"
              type="button"
              class="ps-act"
              @click.stop="handleSubmit(record)"
            >
              提交
            </button>
            <button
              v-if="canAudit(record)"
              type="button"
              class="ps-act"
              @click.stop="goAudit(record)"
            >
              审核
            </button>
            <button
              v-if="canSetApplyLevel(record)"
              type="button"
              class="ps-act"
              @click.stop="openSetLevel(record)"
            >
              设置级别
            </button>
          </template>
        </ProjectCardGrid>
      </a-spin>

      <div class="ps-overview__pager" v-if="pagination.total > 0">
        <a-pagination
          :current="pagination.current"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          show-size-changer
          :page-size-options="['6', '12', '24', '48']"
          :locale="paginationLocale"
          @change="onPageChange"
          @showSizeChange="onPageSizeChange"
        />
      </div>
    </div>

    <identitypdf ref="identitypdf" @importsuccess="onImportSuccess"></identitypdf>
    <a-modal
      v-model="levelVisible"
      title="设置项目级别"
      ok-text="确认"
      cancel-text="取消"
      @ok="confirmSetLevel"
      @cancel="levelVisible = false"
    >
      <p>请选择该项目级别：</p>
      <a-radio-group v-model="levelValue" name="applyLevel">
        <a-radio :value="1">省级</a-radio>
        <a-radio :value="2">市级</a-radio>
      </a-radio-group>
    </a-modal>
  </div>
</template>

<script>
import {
  lxApplyApplylist,
  lxApplyjApplylist,
  lxApplyApplyDelete,
  applyDateView,
  setApplyLevel,
} from "@/api/index";
import { postAction } from "@/api";
import { getApplyStatCount, getModuleChangeCount, sumPendingReviewNum, getApplyOverviewList, canRoleAuditAtLevel } from "@/api/auditTask.js";
import { getTsFormatDate } from "@/utils/time";
import identitypdf from "@/components/common/identityPdf.vue";
import ProjectStatBar from "./ProjectStatBar.vue";
import ProjectFilterBar from "./ProjectFilterBar.vue";
import ProjectCardGrid from "./ProjectCardGrid.vue";
import { getModuleConfig, AUDIT_STATE, parseCourseAuditFilter, isTwoLevelAuditModule, getCourseAuditStateText } from "./moduleConfig.js";
import zhCN from "ant-design-vue/es/locale/zh_CN";

export default {
  name: "SchoolProjectList",
  components: {
    ProjectStatBar,
    ProjectFilterBar,
    ProjectCardGrid,
    identitypdf,
  },
  props: {
    moduleType: {
      type: String,
      default: "studio",
    },
  },
  data() {
    return {
      paginationLocale: zhCN.Pagination,
      loading: false,
      dataList: [],
      selectedIds: [],
      btnShow: true,
      startTime: "",
      endTime: "",
      timestampStartTime: "",
      timestampEndTime: "",
      filters: {
        keyword: "",
        auditState: "",
        year: null,
      },
      pagination: {
        current: 1,
        pageSize: 6,
        total: 0,
      },
      stats: {
        total: 0,
        approved: 0,
        pending: 0,
        requests: 0,
      },
      // 设置项目级别
      levelVisible: false,
      levelValue: 1,
      levelRecord: null,
    };
  },
  computed: {
    config() {
      return getModuleConfig(this.moduleType);
    },
    isSchoolRole() {
      return this.$store.state.role === "school-role";
    },
    isAuditRole() {
      return this.$store.state.role === "audit-role";
    },
    isEducationRole() {
      return (
        this.$store.state.role === "audit-role" ||
        this.$store.state.role === "education-role" ||
        this.$store.state.role === "education-guide-role"
      );
    },
    isTwoLevel() {
      return isTwoLevelAuditModule(this.moduleType);
    },
    /** 审核员只审不申报，不展示新增/编辑/移除 */
    canManageProjects() {
      return (
        this.$store.state.role === "school-role" ||
        this.$store.state.role === "teacher-role"
      );
    },
    /** 两级审核模块不展示批量提交；其它模块仍仅学校可批量提交 */
    canBatchSubmit() {
      if (this.isTwoLevel) return false;
      return this.isSchoolRole;
    },
    /** 工作室/品牌/微课：学校/教师/教育厅/局 均走总览接口 */
    useOverviewList() {
      return (
        this.isTwoLevel &&
        (this.$store.state.role === "school-role" ||
          this.$store.state.role === "teacher-role" ||
          this.isEducationRole)
      );
    },
    /** 审核员沿用 jyjList，卡片 UI 与学校一致 */
    useAuditList() {
      return false;
    },
  },
  mounted() {
    this.fetchApplyDate();
    this.fetchList();
    this.fetchStatCount();
  },
  methods: {
    canEdit(record) {
      if (!this.canManageProjects) return false;
      const s = String(record.auditState);
      return s === AUDIT_STATE.DRAFT || s === AUDIT_STATE.REJECTED;
    },
    canRemove(record) {
      if (!this.canManageProjects) return false;
      const s = String(record.auditState);
      return s !== AUDIT_STATE.PENDING && s !== AUDIT_STATE.APPROVED;
    },
    canSelect(record) {
      const s = String(record.auditState);
      return s !== AUDIT_STATE.PENDING && s !== AUDIT_STATE.APPROVED;
    },
    canSubmit(record) {
      if (this.isTwoLevel) return false;
      const role = this.$store.state.role;
      if (role !== "school-role" && role !== "teacher-role") return false;
      const s = String(record.auditState);
      return s === AUDIT_STATE.DRAFT || s === AUDIT_STATE.REJECTED;
    },
    /**
     * 与卡片状态文案绑定：
     * 学校 → 仅「待学校审核」；审核员 → 仅「待教育局/厅审核」
     */
    canAudit(record) {
      if (!this.isTwoLevel) return false;
      const role = this.$store.state.role;
      // education-role 教育局：与 guide 一致仅只读，不提供审核操作
      if (role === "education-role") return false;
      const stageText = getCourseAuditStateText(record);
      if (role === "school-role") return stageText === "待学校审核";
      if (role === "audit-role") return stageText === "待教育局/厅审核";
      return canRoleAuditAtLevel(role, record.auditState, record.auditLevel);
    },
    /** 终审通过(auditState=2)的工作室/品牌/微课可设置级别；
     *  仅审核员(audit-role)可设置；education-role/education-guide-role 只读 */
    canSetApplyLevel(record) {
      const role = this.$store.state.role;
      if (role !== "audit-role") return false;
      return String(record.auditState) === AUDIT_STATE.APPROVED;
    },
    openSetLevel(record) {
      this.levelRecord = record;
      this.levelValue = Number(record.applyLevel) === 2 ? 2 : 1;
      this.levelVisible = true;
    },
    confirmSetLevel() {
      const record = this.levelRecord;
      if (!record || !record.id) {
        this.$message.warning("未找到要设置级别的项目");
        return;
      }
      setApplyLevel({
        applyIds: [record.id],
        applyLevel: this.levelValue,
      }).then((res) => {
        if (res.code === 200) {
          this.$message.success("设置级别成功");
          this.levelVisible = false;
          this.levelRecord = null;
          this.fetchList();
          this.fetchStatCount();
        } else {
          this.$message.error(res.message || "设置级别失败");
        }
      });
    },
    onMoreAction({ key }, record) {
      if (key === "edit") this.handleEdit(record);
      if (key === "remove") this.handleRemove(record);
    },
    /** 列表页审核按钮：仅进入审核页面，通过/驳回在审核页内操作 */
    goAudit(record) {
      if (!this.canAudit(record)) {
        this.$message.warning("当前阶段不可审核");
        return;
      }
      this.goUpload(record, "audit");
    },
    toggleSelect(record) {
      const id = record.id;
      const i = this.selectedIds.indexOf(id);
      if (i >= 0) this.selectedIds.splice(i, 1);
      else this.selectedIds.push(id);
    },
    onCardSelectChange(record, checked) {
      const id = record.id;
      const i = this.selectedIds.indexOf(id);
      if (checked && i < 0) this.selectedIds.push(id);
      if (!checked && i >= 0) this.selectedIds.splice(i, 1);
    },
    onSelectAll(checked) {
      const ids = this.dataList.filter((r) => this.canSelect(r)).map((r) => r.id);
      if (checked) {
        const set = {};
        this.selectedIds.forEach((id) => {
          set[id] = true;
        });
        ids.forEach((id) => {
          set[id] = true;
        });
        this.selectedIds = Object.keys(set);
      } else {
        const remove = {};
        ids.forEach((id) => {
          remove[id] = true;
        });
        this.selectedIds = this.selectedIds.filter((id) => !remove[id]);
      }
    },
    buildQuery(page, size) {
      const data = {
        applyType: this.config.applyType,
        pageNo: page,
        pageSize: size,
      };
      if (this.filters.keyword) {
        data.name = this.filters.keyword;
        data.keyword = this.filters.keyword;
      }
      if (this.filters.auditState) {
        if (this.isTwoLevel) {
          const parsed = parseCourseAuditFilter(this.filters.auditState);
          if (parsed.auditState) data.auditState = parsed.auditState;
          if (parsed.auditLevel != null) data.auditLevel = parsed.auditLevel;
        } else {
          data.auditState = this.filters.auditState;
        }
      }
      if (this.filters.year) {
        // 与原 tableView 一致：从年份选择器取值
        const y = this.filters.year;
        if (y && typeof y.year === "function") {
          data.applyYear = String(y.year());
        } else {
          data.applyYear = String(new Date(y).getFullYear());
        }
      }
      if (this.isSchoolRole) {
        data.roleCode = "school";
      }
      // 教育厅/局角色：后端按 roleCode=education 限制只能查看
      // 待教育局审核(1+level2)/审核通过(2)/教育局驳回(3+level2)
      if (this.isEducationRole) {
        data.roleCode = "education";
      }
      return data;
    },
    fetchList() {
      this.loading = true;
      const data = this.buildQuery(
        this.pagination.current,
        this.pagination.pageSize
      );
      const req = this.useOverviewList
        ? getApplyOverviewList(data)
        : this.useAuditList
          ? lxApplyjApplylist(data)
          : lxApplyApplylist(data);
      req
        .then((res) => {
          if (res.code === 0 || res.code === 200) {
            const result = res.result || {};
            const records = Array.isArray(result)
              ? result
              : result.records || [];
            this.dataList = records.map((r) => {
              const id = r.id || r.applyId;
              return {
                ...r,
                id,
                applyId: r.applyId || r.id,
                name: r.name || r.applyName,
                auditState:
                  r.auditState != null && r.auditState !== ""
                    ? r.auditState
                    : r.status,
                auditNum:
                  Number(r.auditNum) ||
                  Number(r.pendingCount) ||
                  0,
              };
            });
            this.pagination.total = Array.isArray(result)
              ? records.length
              : result.total || 0;
            this.fetchChangeCounts(this.dataList);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** 列表每条卡片的变更请求数：待审核 + 教师/学校含待提交草稿 */
    fetchChangeCounts(list) {
      if (!list || !list.length) return;
      const role = this.$store.state.role;
      const includeDraft =
        role === "teacher-role" || role === "school-role";
      list.forEach((record) => {
        if (!record.id) return;
        getModuleChangeCount(record.id, 0)
          .then((res) => {
            if (res.code === 200 || res.code === 0) {
              const count = sumPendingReviewNum(res.result, includeDraft);
              const i = this.dataList.findIndex((r) => r.id === record.id);
              if (i >= 0) this.$set(this.dataList[i], "auditNum", count);
            }
          })
          .catch(() => {});
      });
    },
    fetchStatCount() {
      const params = { applyType: this.config.applyType };
      // 教育厅/局角色：与列表权限一致，仅统计 待教育局审核/审核通过/教育局驳回
      if (this.isEducationRole) {
        params.roleCode = "education";
      }
      getApplyStatCount(params).then((res) => {
        if (res.code === 200 || res.code === 0) {
          const r = res.result || {};
          this.stats = {
            total: r.totalCount || 0,
            approved: r.approvedCount || 0,
            pending: r.pendingCount || 0,
            requests: r.changeRequestCount || 0,
          };
        }
      });
    },
    fetchApplyDate() {
      applyDateView().then((res) => {
        if (res.code == 200) {
          this.timestampStartTime = res.result.startTime;
          this.timestampEndTime = res.result.endTime;
          const startTime = parseInt(res.result.startTime);
          const endTime = parseInt(res.result.endTime);
          this.startTime = getTsFormatDate(startTime);
          this.endTime = getTsFormatDate(endTime);
          const timestamp = Date.parse(new Date());
          if (
            timestamp > this.timestampStartTime &&
            timestamp < this.timestampEndTime
          ) {
            this.btnShow = true;
          } else {
            this.btnShow = false;
          }
        }
      });
    },
    onFilterChange() {
      this.pagination.current = 1;
      this.selectedIds = [];
      this.fetchList();
    },
    /** 点击搜索图标：直接调接口（关键词为空也查询全部，不做判空） */
    onSearch() {
      this.pagination.current = 1;
      this.fetchList();
    },
    onPageChange(page, pageSize) {
      this.pagination.current = page;
      this.pagination.pageSize = pageSize;
      this.fetchList();
    },
    onPageSizeChange(current, size) {
      this.pagination.current = 1;
      this.pagination.pageSize = size;
      this.fetchList();
    },
    goUpload(record, news) {
      this.$router.push({
        name: this.config.routeUpload,
        query: {
          id: record.id,
          applyType: this.config.applyType,
          new: news,
        },
      });
    },
    goDetail(record, tab) {
      if (
        this.isTwoLevel &&
        String(record.auditState) !== AUDIT_STATE.APPROVED
      ) {
        return;
      }
      this.$router.push({
        name: this.config.routeDetail,
        query: {
          id: record.id,
          applyType: record.applyType || this.config.applyType,
          name: record.name,
          tab: tab || "current",
        },
      });
    },
    handleAdd() {
      if (this.$refs.identitypdf && this.$refs.identitypdf.init) {
        this.$refs.identitypdf.init("add", { name: this.config.routeList });
      } else {
        this.$router.push({
          name: this.config.routeUpload,
          query: { applyType: this.config.applyType, new: "new" },
        });
      }
    },
    onImportSuccess() {
      this.fetchList();
      this.fetchStatCount();
    },
    handleEdit(record) {
      this.goUpload(record, "edit");
    },
    handleSubmit(record) {
      const that = this;
      this.$confirm({
        title:
          this.isTwoLevel
            ? this.isSchoolRole
              ? "确认提交该项目？提交后将进入待教育局/厅审核状态。"
              : "确认提交该项目？提交后将进入待学校审核状态。"
            : "确认提交该项目？提交后将进入待审核状态。",
        okText: "确认",
        cancelText: "取消",
        onOk() {
          return postAction("/lx/lxApply/submitBatch", {
            applyType: that.config.applyType,
            applyIds: [record.id],
          }).then((res) => {
            if (res.code === 200) {
              that.$message.success("提交成功");
              that.fetchList();
              that.fetchStatCount();
            } else {
              that.$message.error(res.message || "提交失败");
            }
          });
        },
      });
    },
    handlePreview(record) {
      this.goDetail(record, "current");
    },
    handleFormPreview(record) {
      this.goUpload(record, "preview");
    },
    handleRemove(record) {
      const that = this;
      this.$confirm({
        title: "确认移除该项目？移除后不可找回。",
        okText: "确认",
        cancelText: "取消",
        onOk() {
          return lxApplyApplyDelete({ id: record.id }).then((res) => {
            if (res.code === 200) {
              that.$message.success(res.message || "移除成功");
              that.fetchList();
              that.fetchStatCount();
            } else {
              that.$message.error(res.message || "移除失败");
            }
          });
        },
      });
    },
    handleBatchSubmit() {
      if (!this.selectedIds.length) {
        this.$message.warning("请先勾选要提交的项目");
        return;
      }
      const params = {
        applyType: this.config.applyType,
        applyIds: this.selectedIds,
      };
      postAction("/lx/lxApply/submitBatch", params).then((res) => {
        if (res.code === 200) {
          this.$message.success("批量提交成功");
          this.selectedIds = [];
          this.fetchList();
          this.fetchStatCount();
        } else {
          this.$message.error(res.message || "提交失败");
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-overview {
  width: 100%;
  // padding: 16px 12px 12px;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
  &__time {
    font-size: 13px;
    color: #4b5563;
    margin-bottom: 12px;
  }
  &__time-warn {
    color: #ea580c;
    margin-left: 8px;
  }
  &__main {
    // box-shadow: 0 0 8px 0 rgba(115, 155, 220, 0.35);
    background-color: rgba(255, 255, 255, 0.96);
    border-radius: 8px;
    border: 1px solid rgba(229, 229, 229, 0.96);
    padding-bottom: 8px;
    overflow: hidden;
  }
  &__pager {
    display: flex;
    justify-content: flex-end;
    padding: 8px 15px 16px;
    background: transparent;
  }
  &.is-course-page {
    font-size: 16px;
    .ps-overview__time {
      font-size: 15px;
    }
    .ps-act {
      font-size: 14px;
    }
    ::v-deep .ps-stat-item__num {
      font-size: 28px;
    }
    ::v-deep .ps-stat-item__lbl {
      font-size: 14px;
    }
    ::v-deep .ps-filter-bar__year-label,
    ::v-deep .ps-input,
    ::v-deep .ps-select,
    ::v-deep .ps-filter-bar .ps-btn {
      font-size: 15px;
    }
    ::v-deep .ant-calendar-picker-input,
    ::v-deep .ant-pagination,
    ::v-deep .ant-select,
    ::v-deep .ant-select-selection {
      font-size: 15px;
    }
    ::v-deep .ps-select-all {
      font-size: 15px;
    }
    ::v-deep .ps-card__name {
      font-size: 18px;
    }
    ::v-deep .ps-card__meta,
    ::v-deep .ps-card__summary {
      font-size: 14px;
    }
    ::v-deep .ps-card__btn {
      font-size: 14px;
    }
    ::v-deep .ps-badge {
      font-size: 14px;
    }
    ::v-deep .ps-empty {
      font-size: 16px;
    }
    ::v-deep .ps-empty__big {
      font-size: 42px;
    }
  }
}
.ps-more {
  flex: 1 1 0;
  min-width: 0;
  ::v-deep .ant-dropdown-trigger {
    display: block;
    width: 100%;
  }
}
.ps-more-btn {
  width: 100%;
  height: 33px;
  padding: 0 6px;
  margin: 0;
  border: 1px solid #3870ed;
  background: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
  line-height: 1;
  cursor: pointer;
  color: #000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  box-sizing: border-box;
  &:hover {
    background: #eff6ff;
  }
  .anticon {
    font-size: 12px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
  }
}
.ps-more-danger {
  color: #ef4444;
}
.ps-act {
  flex: 1 1 0;
  min-width: 0;
  height: 33px;
  padding: 0 6px;
  margin: 0;
  border: 1px solid #3870ed;
  background: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-family: "Microsoft YaHei", "微软雅黑", sans-serif;
  line-height: 1;
  cursor: pointer;
  color: #000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  box-sizing: border-box;
  &:hover {
    background: #eff6ff;
  }
  &--danger {
    color: #ef4444;
    border-color: #ef4444;
    &:hover {
      background: #fef2f2;
      border-color: #ef4444;
      color: #ef4444;
    }
  }
}
</style>
